'use client';

import { MAX_FILES_LENGTH } from '@/constants';
import { usePathname } from '@/i18n/navigation';
import { cn, truncateFileName } from '@/lib/utils';
import { contactSchema } from '@/lib/validationSchema';
import { PathEnum } from '@/types/enums';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import type { Company } from '@/types/content';
import CompanyDetailsView from '../blocks/CompanyDetailsView';
import FormSuccessMessage from '../blocks/FormSuccessMessage';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import FileInput from '../ui/FileInput';
import FilePreview from '../ui/FilePreview';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import TextSmall from '../ui/TextSmall';

type FormData = z.infer<typeof contactSchema>;

export default function ContactForm({
    className,
    company,
}: {
    className?: string;
    company: Company;
}) {
    const t = useTranslations();
    const fileUploaderRef = useRef<HTMLInputElement>(null);
    const pathname = usePathname();

    const isContactPage = `/${pathname.toLowerCase()}/` === PathEnum.Contact;

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [filePreviews, setFilePreviews] = useState<File[]>([]);
    const [filesError, setFilesError] = useState<string | null>(null);

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        resolver: zodResolver(contactSchema),
    });

    const onSubmit = async (data: FormData) => {
        setSubmitError(null);

        const formData = new FormData();
        formData.append('formType', 'contact');
        formData.append('name', data.name);
        formData.append('email', data.email);
        formData.append('phone', data.phone || '');
        formData.append('comment', data.comment);
        formData.append('consent', String(data.consent));

        filePreviews.forEach((file) => {
            formData.append('files[]', file);
        });

        try {
            const response = await fetch('/submit-form.php', {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                reset();
                setFilePreviews([]);
                setFilesError(null);
                setIsSubmitted(true);
            } else {
                console.error('Form submission failed:', response.statusText);
                setSubmitError(t('Form.submissionError'));
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitError(t('Form.submissionError'));
        }
    };

    const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFilesArray = Array.from(e.target.files);
            const updatedFilesArray = [...filePreviews, ...newFilesArray];

            if (updatedFilesArray.length > MAX_FILES_LENGTH) {
                setFilesError(t('Form.maxFilesExceeded'));
            } else {
                setFilesError(null);
                setFilePreviews(updatedFilesArray);
            }
        }
    };

    const handleRemoveFile = (index: number) => {
        setFilePreviews((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message ? t(`FieldErrors.${errors[fieldName]?.message}`) : '';

    if (isSubmitted) {
        return <FormSuccessMessage className={className} id="get-offer-form-trigger" />;
    }

    return (
        <form
            className={cn(
                'flex flex-col max-lg:py-6 max-lg:px-4 max-lg:gap-6 max-lg:bg-white/80 max-lg:backdrop-blur-sm lg:p-[60px] bg-white bg-opacity-90 shadow-m backdrop-blur-md backdrop-brightness-100 gap-12 relative z-10',
                className,
            )}
            onSubmit={handleSubmit(onSubmit)}
            id="get-offer-form-trigger"
        >
            <div>
                <h3 className="text-dark-grey drop-shadow-sm leading-tight text-h1 max-lg:text-h2">
                    {t.rich(`Form.${isContactPage ? 'requestOffer' : 'visitUs'}`, {
                        strong: (chunks) => (
                            <span className="font-medium text-brand">{chunks}</span>
                        ),
                    })}
                </h3>
                <TextSmall>{t('Form.reachUs')}</TextSmall>
            </div>
            <CompanyDetailsView company={company} />
            <div>
                <div className="flex items-end justify-between w-full max-lg:gap-4 lg:gap-[146px] max-lg:flex-col [&>div]:w-full">
                    <Input
                        {...register('name')}
                        type="text"
                        name="name"
                        id="name"
                        placeholder={t('Form.fullName')}
                        autoComplete="false"
                        error={getFieldError('name')}
                        required
                    />
                    <Input
                        {...register('email')}
                        type="email"
                        name="email"
                        id="email"
                        placeholder={t('Form.yourEmail')}
                        autoComplete="false"
                        error={getFieldError('email')}
                        required
                    />
                </div>
                <div className="w-full lg:w-1/2 lg:pr-[72px] lg:mt-10 max-lg:mt-4 mt-[30px]">
                    <Input
                        {...register('phone')}
                        type="phone"
                        name="phone"
                        id="phone"
                        placeholder={t('Form.phone')}
                        autoComplete="false"
                        error={getFieldError('phone')}
                    />
                </div>
            </div>
            <Textarea
                {...register('comment')}
                name="comment"
                id="comment"
                placeholder={t('Form.message')}
                autoComplete="false"
                error={getFieldError('comment')}
                rows={1}
                required
            />
            <div>
                <div className="flex items-start max-lg:flex-wrap lg:min-h-[100px]">
                    <div className="flex flex-col mb-2">
                        <FileInput
                            {...register('files')}
                            className="w-fit h-fit sm:mr-12 mr-4 text-nowrap"
                            id="file"
                            name="file"
                            ref={fileUploaderRef}
                            onChange={handleFilesChange}
                            multiple
                            accept="application/pdf"
                        />
                        {filesError && (
                            <TextSmall className="mt-1 text-sm text-red-400">
                                {filesError}
                            </TextSmall>
                        )}
                    </div>
                    {filePreviews.length > 0 && (
                        <div className="flex overflow-x-auto items-center gap-2">
                            {filePreviews.map((file, index) => (
                                <FilePreview
                                    key={index}
                                    className="w-fit mb-2"
                                    fileName={truncateFileName(file.name)}
                                    onRemove={() => handleRemoveFile(index)}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <Checkbox
                    {...register('consent')}
                    className="mt-4"
                    id="consent"
                    name="consent"
                    label={t('Form.consent')}
                    required
                />
            </div>
            <div className="flex max-md:flex-col-reverse items-start justify-between max-lg:gap-4 gap-10">
                <div className="relative" role="alert" aria-live="assertive">
                    {submitError ? <div className="text-red-400">{submitError}</div> : null}
                </div>
                <Button
                    className="w-fit min-w-[100px] md:min-w-40 h-fit self-end"
                    title={t('Buttons.send')}
                    variant="send"
                    withArrow
                    type="submit"
                    disabled={isSubmitting}
                />
            </div>
        </form>
    );
}
